import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import {
  formatDecimalNumber,
  formatPercentage,
} from "@aprao/common/src/numberFormats.js";
import type { AppraisalDocumentFacade } from "@aprao/model/src/stores/AppraisalDocumentFacade.js";
import type { ResidualFacade } from "@aprao/model/src/stores/ResidualFacade.js";
import type { SitePriceManager } from "@aprao/model/src/stores/SitePriceManager.js";

import { useI18n } from "../../common/ApraoI18n.js";
import { useNumberUnits } from "../../common/useNumberUnits.js";
import { buildSitePurchaseDescription } from "../../i18n/buildSitePurchaseDescription.js";
import { labelByUseClass } from "../../i18n/useClassLabels.js";
import { Section } from "../Section.js";
import { SimpleTableRow, SimpleTableSection } from "../SimpleTableRow.js";

export const SitePurchase = (props: {
  appraisalDocument: AppraisalDocumentFacade;
  priceManager: SitePriceManager;
  residual: ResidualFacade;
}) => {
  const { appraisalDocument, priceManager, residual } = props;

  const { currency } = useNumberUnits();
  const { msg } = useI18n();

  return (
    <Section title="Site Purchase">
      {appraisalDocument.sitePurchases.map((site, siteIdx) => (
        <SimpleTableSection key={siteIdx}>
          <SimpleTableRow
            isHeader
            cells={[
              buildSitePurchaseDescription(
                site.$model,
                appraisalDocument.$model.stages.purchase.sitePurchases,
              ),
              null,
              null,
              null,
              null,
              null,
            ]}
          />
          <SimpleTableRow
            cellCount={2}
            cells={[
              `Existing use class: ${labelByUseClass.get(
                residual.getLandTaxBySite(site).useClassCurrent,
              )}`,
              null,
            ]}
          />
          <SimpleTableRow
            borderBottom
            isHeader
            cells={[
              null,
              null,
              null,
              null,
              "of price",
              `Total (${currency.symbol})`,
            ]}
          />
          <SimpleTableRow
            cells={[
              "Site purchase price",
              null,
              null,
              null,
              null,
              formatDecimalNumber(
                priceManager.getPriceBySite(site) ?? FRACTION_ZERO,
              ),
            ]}
          />
          <SimpleTableRow
            cells={[
              msg("global.landTax"),
              null,
              null,
              null,
              formatPercentage(
                residual.getLandTaxRatioBySite(site) ?? FRACTION_ZERO,
              ),
              formatDecimalNumber(
                residual.getLandTaxBySite(site).landTax ?? FRACTION_ZERO,
              ),
            ]}
          />
          {residual.getCustomCostsBySite(site).map((cost, idxCost) => (
            <SimpleTableRow
              key={idxCost}
              cells={[
                cost.named.name,
                null,
                null,
                null,
                formatPercentage(
                  cost.relativesSet.getRelative("ratioPrice") ?? FRACTION_ZERO,
                ),
                formatDecimalNumber(
                  cost.relativesSet.getRelative("absolute") ?? FRACTION_ZERO,
                ),
              ]}
            />
          ))}
          <SimpleTableRow
            bold
            borderTop
            borderBottom
            cells={[
              "Total",
              null,
              null,
              null,
              null,
              formatDecimalNumber(
                residual.getPurchaseCostTotalBySite(site) ?? FRACTION_ZERO,
              ),
            ]}
          />
        </SimpleTableSection>
      ))}
    </Section>
  );
};
